// src/pages/ReferralPage.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';
import { db } from '../firebase';
import { doc, getDoc, updateDoc, collection, query, where, getDocs } from 'firebase/firestore';

const ReferralPage = () => {
  const { user, profile } = useAuth();
  const navigate = useNavigate();
  const [copied, setCopied] = useState('');
  const [referralStats, setReferralStats] = useState({ count: 0, list: [] });
  const [loading, setLoading] = useState(true);

  // 紹介コード = ニックネームベース（英数字のみ・最大10文字）
  const rawNickname = profile?.nickname || profile?.username || profile?.displayName || '';
  const referralCode = rawNickname
    ? rawNickname.replace(/[^a-zA-Z0-9ぁ-んァ-ン一-龥]/g, '').slice(0, 10) || user?.uid?.slice(0, 8).toUpperCase()
    : user?.uid?.slice(0, 8).toUpperCase() || '';
  const referralUrl = `https://www.nobishiro-competency.com/login?ref=${referralCode}&beta=NOBISHIRO2026`;

  useEffect(() => {
    if (!user?.uid) return;
    const fetchStats = async () => {
      try {
        const q = query(collection(db, 'users'), where('referredBy', '==', referralCode));
        const snap = await getDocs(q);
        const list = snap.docs.map(d => ({
          id: d.id,
          nickname: d.data().nickname || d.data().username || '匿名ユーザー',
          createdAt: d.data().createdAt,
        }));
        setReferralStats({ count: list.length, list });
      } catch(e) {}
      setLoading(false);
    };
    fetchStats();
  }, [user?.uid, referralCode]);

  const handleCopy = (type) => {
    const text = type === 'url' ? referralUrl : referralCode;
    navigator.clipboard?.writeText(text);
    setCopied(type);
    setTimeout(() => setCopied(''), 2000);
  };

  const shareTexts = {
    x: `【一緒にやろう】\n私はNOBISHIROコンピテンシー®で学習記録・成長タイプ診断をしています📚\n\nあなたの努力を、見える形に。\n→ ${referralUrl}\n\n#社会人勉強 #スキルアップ #自己成長 #NOBISHIRO`,
    line: `NOBISHIROコンピテンシー®、一緒にやってみませんか？\n\n学習を記録するだけで、成長が見えてきます。\n私の紹介URLからだと全機能を無料で試せます！\n\n→ ${referralUrl}`,
  };

  const archetypeNames = {
    LOGICAL_ARCHITECT: '論理的設計者', EMPATHETIC_CONNECTOR: '共感的連結者',
    BOLD_PIONEER: '大胆な開拓者', FLEXIBLE_ARTISAN: '柔軟な実践者',
  };
  const arcName = archetypeNames[profile?.growthWallProfile?.archetypeKey] || '成長タイプ診断済み';

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Hiragino Sans','Yu Gothic',sans-serif" }}>
      {/* ヘッダー */}
      <div style={{ background: 'linear-gradient(135deg,#4f46e5,#7c3aed)', color: '#fff', padding: '20px 20px 32px' }}>
        <button onClick={() => navigate('/dashboard')} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: 8, padding: '6px 12px', color: '#fff', cursor: 'pointer', fontSize: 13, fontWeight: 600, marginBottom: 20 }}>
          ← ダッシュボードへ戻る
        </button>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: 11, fontWeight: 700, letterSpacing: '0.1em', opacity: 0.8, marginBottom: 8 }}>紹介プログラム</div>
          <h1 style={{ fontSize: 24, fontWeight: 900, marginBottom: 8 }}>仲間を誘って、一緒に成長しよう</h1>
          <p style={{ fontSize: 14, opacity: 0.85, lineHeight: 1.6 }}>あなたの紹介URLから登録した方は、全機能を無料でお試しいただけます</p>
        </div>
      </div>

      <div style={{ maxWidth: 600, margin: '0 auto', padding: '24px 16px 60px' }}>

        {/* 紹介コード */}
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: '#6b7280', marginBottom: 12 }}>あなたの紹介URL</div>
          <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: 10, padding: '12px 14px', fontFamily: 'monospace', fontSize: 12, color: '#374151', wordBreak: 'break-all', marginBottom: 10 }}>
            {referralUrl}
          </div>
          <button onClick={() => handleCopy('url')} style={{ width: '100%', padding: '12px', background: copied === 'url' ? '#059669' : '#4f46e5', color: '#fff', border: 'none', borderRadius: 10, fontSize: 14, fontWeight: 700, cursor: 'pointer', transition: 'background 0.2s' }}>
            {copied === 'url' ? '✓ コピーしました！' : 'URLをコピーする'}
          </button>
        </div>

        {/* 統計 */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
          <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: 36, fontWeight: 900, color: '#4f46e5', fontFamily: 'sans-serif' }}>{loading ? '...' : referralStats.count}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>紹介した仲間</div>
          </div>
          <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 14, padding: '20px', textAlign: 'center' }}>
            <div style={{ fontSize: 18, fontWeight: 900, color: '#7c3aed', fontFamily: 'sans-serif', wordBreak: 'break-all' }}>{referralCode}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 4 }}>あなたのコード</div>
          </div>
        </div>

        {/* 特典セクション */}
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 14 }}>
            🎁 紹介特典
          </div>
          {[
            { count: 1, reward: 'Standardプラン 1ヶ月無料', color: '#4f46e5', bg: '#ede9fe', done: referralStats.count >= 1 },
            { count: 3, reward: 'Standardプラン 3ヶ月無料', color: '#7c3aed', bg: '#f5f3ff', done: referralStats.count >= 3 },
            { count: 5, reward: 'Businessプラン 1ヶ月無料', color: '#059669', bg: '#d1fae5', done: referralStats.count >= 5 },
          ].map((tier, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px', borderRadius: 10, background: tier.done ? tier.bg : '#f9fafb', border: `1px solid ${tier.done ? tier.color+'40' : '#e5e7eb'}`, marginBottom: 8, opacity: tier.done ? 1 : 0.6 }}>
              <div style={{ width: 32, height: 32, borderRadius: '50%', background: tier.done ? tier.color : '#e5e7eb', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#fff', fontSize: 14, fontWeight: 900, flexShrink: 0 }}>
                {tier.done ? '✓' : tier.count}
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: tier.done ? tier.color : '#374151' }}>{tier.reward}</div>
                <div style={{ fontSize: 11, color: '#9ca3af' }}>{tier.count}名紹介で獲得{tier.done ? ' · 達成済み🎉' : ` · あと${tier.count - referralStats.count}名`}</div>
              </div>
            </div>
          ))}
          <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 8, lineHeight: 1.6 }}>
            ※ 特典は運営が確認後、手動でプランを付与いたします。紹介数が達成したら富永勇貴までご連絡ください。
          </p>
        </div>

        {/* SNSシェアボタン */}
        <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px', marginBottom: 16 }}>
          <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 14 }}>SNSで紹介する</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            <button onClick={() => window.open('https://twitter.com/intent/tweet?text='+encodeURIComponent(shareTexts.x), '_blank')}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: '#000', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}>
              <span style={{ fontSize: 18, fontWeight: 900 }}>𝕏</span>
              <div>
                <div style={{ fontWeight: 700 }}>X (Twitter) で紹介</div>
                <div style={{ fontSize: 11, opacity: 0.7, fontWeight: 400 }}>フォロワーに紹介する</div>
              </div>
            </button>
            <button onClick={() => window.open('https://social-plugins.line.me/lineit/share?url='+encodeURIComponent(referralUrl)+'&text='+encodeURIComponent(shareTexts.line), '_blank')}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: '#06C755', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'left' }}>
              <span style={{ fontSize: 18, fontWeight: 900 }}>L</span>
              <div>
                <div style={{ fontWeight: 700 }}>LINE で紹介</div>
                <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 400 }}>友達に直接送る</div>
              </div>
            </button>
            <button onClick={() => { navigator.clipboard?.writeText(shareTexts.x); setCopied('text'); setTimeout(()=>setCopied(''), 2000); }}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: copied === 'text' ? '#059669' : '#6b7280', color: '#fff', border: 'none', borderRadius: 10, padding: '13px 16px', fontSize: 14, fontWeight: 700, cursor: 'pointer', textAlign: 'left', transition: 'background 0.2s' }}>
              <span style={{ fontSize: 18 }}>🔗</span>
              <div>
                <div style={{ fontWeight: 700 }}>{copied === 'text' ? 'コピーしました！' : 'テキストをコピー'}</div>
                <div style={{ fontSize: 11, opacity: 0.85, fontWeight: 400 }}>Instagram・他SNSに貼り付け</div>
              </div>
            </button>
          </div>
        </div>

        {/* 紹介した仲間一覧 */}
        {referralStats.count > 0 && (
          <div style={{ background: '#fff', border: '1.5px solid #e5e7eb', borderRadius: 16, padding: '20px' }}>
            <div style={{ fontSize: 13, fontWeight: 800, color: '#111827', marginBottom: 12 }}>紹介した仲間 ({referralStats.count}名)</div>
            {referralStats.list.map((u, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: i < referralStats.list.length - 1 ? '1px solid #f3f4f6' : 'none' }}>
                <div style={{ width: 36, height: 36, borderRadius: '50%', background: '#ede9fe', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, flexShrink: 0 }}>🌱</div>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#111827' }}>{u.nickname}</div>
                  <div style={{ fontSize: 11, color: '#9ca3af' }}>登録済み</div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* 使い方ガイドリンク */}
        <div style={{ marginTop: 16, textAlign: 'center' }}>
          <a href="/guide" style={{ fontSize: 13, color: '#6b7280', textDecoration: 'none' }}>
            使い方ガイドを見る →
          </a>
        </div>

      </div>
    </div>
  );
};

export default ReferralPage;
